<template>
  <div id="rollouts-table">
    <v-row>
      <v-col cols="3" class="pl-6">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
        ></v-text-field>
      </v-col>
      <v-col cols="3">
        <v-select 
          v-model="filterDeviceModel" 
          :items="filterOptions"
          item-text="label"
          item-value="value"
          label="Device Model"
        ></v-select>
      </v-col>
      <v-spacer/>
    </v-row>
    <br>
    <v-data-table 
        :headers="headers" 
        :items="rolloutsByModel" 
        :loading="loading" 
        :search="search"
        class="elevation-1 admin-table"
      >
        <template v-slot:item.withdrawn_on_formatted="{item}">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <span 
                v-bind="attrs"
                v-on="on">
                {{ item.withdrawn_on_formatted }}
              </span>
            </template>
            <span v-if="Object.hasOwn(item, 'status_last_updated_by')">{{ item.status_last_updated_by[0] }}</span>
          </v-tooltip>
        </template>
        <template v-slot:progress>
          <v-progress-linear color="blue" height="2" indeterminate></v-progress-linear>
        </template>
        <template v-slot:item.action="{item}">
				<div class="reports-action-launch" @click="launchRowAction">
					<v-icon>mdi-dots-vertical</v-icon>
					<ActionContainer :ref="item.key" :heading="'Action'" class="report-action-options">
						<ActionItem 
							label="Rollout Details"
							icon="mdi-eye"
							@click.native="toggleRolloutDetails(item)"
            />
            <ActionItem 
              v-if="item.rollout_status != 'Withdrawn'"
              label="Withdraw Rollout"
              icon="mdi-minus-circle"
              :disabled="!$can('modifyFirmwareRollout')"
              @click.native="toggleWithdrawPrompt(item)"
            />
					</ActionContainer>
				</div>
			</template>

     
      
    </v-data-table>

    <v-row>
      <v-spacer/>
      <v-col cols="8" class="vert-center">
        <div class="aux-pagination-btn">
          <v-btn text>
            <v-icon color="#0000008a" @click="setPage(1)">
              mdi-chevron-double-left
            </v-icon>
          </v-btn>
        </div>
        <v-pagination
          v-model="selectedPage"
          :length="totalPages"
          :total-visible="7"
        ></v-pagination>
        <div class="aux-pagination-btn">
          <v-btn text>
            <v-icon color="#0000008a" @click="setPage(totalPages)">
              mdi-chevron-double-right
            </v-icon>
          </v-btn>
        </div>
      </v-col>
      <v-col cols="1" class="pr-12">
        <v-text-field 
          v-model="selectedPage"
          hide-spin-buttons
          min="0"
          type="number"
          :rules="rules"
        />
      </v-col>
    </v-row>

    <Dialog
      :display="withdrawRolloutConfirm"
      :title="'Withdraw Rollout'"
      :state="withdrawState" 
      :res="withdrawRes"
      @close="toggleWithdrawPrompt"
    >
      <v-row>
        <v-col cols="2" class="text-center">
          <v-icon large color="orange">mdi-alert</v-icon>
        </v-col>
        <v-col cols="10">
          Withdrawing this rollout will make the associated firmware build unavailable for automatic firmware update.
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="2">
          
        </v-col>
        <v-col cols="10">
          Are you sure you want to proceed?
        </v-col>
      </v-row>
      <v-row>
        <v-spacer></v-spacer>
        <v-btn text @click="toggleWithdrawPrompt">no</v-btn>
        <v-btn text :loading="withdrawState" @click="withdrawRolout">yes</v-btn>
      </v-row>
    </Dialog>

    <Dialog
      :display="displayRolloutDetails"
      :title="'Rollout Details'"
      :state="rolloutDetailsState" 
      :res="rolloutDetailsRes"
      :width="1000"
      @close="toggleRolloutDetails(null)"
    >
      <RolloutDetails
        v-if="displayRolloutDetails"
        :is-open="displayRolloutDetails"
        :firmware-details="selectedFirmwareDetails"
      />
    </Dialog>
  </div>
</template>

<script>

import Dialog from '@/components/general/Dialog.vue';
import ActionContainer from '@/components/general/ActionContainer.vue';
import ActionItem from '@/components/general/ActionItem.vue';
import RolloutDetails from '../components/RolloutDetails.vue';

import { getFirmwareRollouts, updateFirmwareRollout } from "@/services/API.js"

import moment from "moment";

import { Logger } from "aws-amplify"
const logger = new Logger("firmware / rollouts tab")

export default {
  name: 'Rollouts',

  components: {
    Dialog,
    ActionContainer,
    ActionItem,
    RolloutDetails
  },

  props: {
    refreshRollouts: {
      type: Number,
      required: false,
      default: 0
    }
  },

  data() {
    return {
      headers: [
        { text: "Created On", value: "created_on_formatted" },
        { text: "Created By", value: "created_by_email" },
        { text: "Model", value: "device_model" },
        { text: "FW Version", value: "fw_build_version" },
        { text: "Description", value: "description" },
        { text: "Rollout Status", value: "rollout_status"},
        { text: "Rollout Start", value: "rollout_start_formatted"},
        { text: "Withdrawn", value: "withdrawn_on_formatted"},
        { text: "Actions", value: "action", sortable: false }
      ],
      rollouts: [],
      loading: false,
      search: '',
      withdrawState: false,
      withdrawRes: null,
      withdrawRolloutConfirm: false,
      withdrawTarget: {},
      filterOptions: [
        {
          label: 'All',
          value: 'all'
        },
        {
          label: 'CAMERAMATICS_JH8_NT-HD-4G (NextGen 8CH)',
          value: 'CAMERAMATICS_JH8_NT-HD-4G'
        },
        {
          label: 'CAMERAMATICS_JH4_NT-HD-4G (NextGen 4CH)',
          value: 'CAMERAMATICS_JH4_NT-HD-4G'
        },
        {
          label: 'CAMERAMATICS_D700_SD-4G (Genie Dashcam)',
          value: 'CAMERAMATICS_D700-SD-4G'
        },
        {
          label: 'CAMERAMATICS_JI7A_SD-4G (Genie 2 Dashcam)',
          value: 'CAMERAMATICS_JI7A-SD-4G'
        },
        {
          label: 'CAMERAMATICS_JH8_HD-4G (Legacy 8CH)',
          value: 'CAMERAMATICS_JH8-HD-4G'
        },
        {
          label: 'CAMERAMATICS_JH4_HD-4G (Legacy 4CH)',
          value: 'CAMERAMATICS_JH4-HD-4G'
        }
      ],
      filterDeviceModel: 'all',
      page: 1,
      totalPages: 1,
      rules: [
        v => ( v && v >= 1 ) || `First page cannot be less than 1`,
        v => ( v && v <= this.totalPages ) || `Cannot exceed page number greater than ${this.totalPages}`,
      ],
      displayRolloutDetails: false,
      selectedFirmwareDetails: {},
      rolloutDetailsState: false,
      rolloutDetailsRes: null,
      refreshBuilds: 0
    }
  },

  computed: {
    rolloutsByModel() {
      if(this.filterDeviceModel == 'all') {
        return this.rollouts
      }
      else {
        return this.rollouts.filter(item => item.device_model == this.filterDeviceModel)
      }
    },

    selectedPage: {
      get() { 
        return parseInt(this.page); 
      },
      set(newVal) { 
        this.page = parseInt(newVal);
      }
    }
  },

  watch: {
    page() {
      if(this.page > 0 && this.page <= this.totalPages) {
        this.getRollouts()
      }
    },

    refreshRollouts() {
      this.getRollouts()
    },

    refreshBuilds() {
      this.$emit('refreshBuilds', this.refreshBuilds)
    }
  },

  created() {
    this.getRollouts()
  },

  methods: {
    toggleRolloutDetails(item) {
      logger.info('toggleRolloutDetails', item)

      this.displayRolloutDetails = !this.displayRolloutDetails
      if(item) {
        this.selectedFirmwareDetails = item
      }
    },
    async getRollouts() {
      this.loading = true

      try {
        const rollouts = await getFirmwareRollouts(this.page)

        logger.info('rollouts', rollouts)
        this.page = rollouts.page
        this.totalPages = rollouts.page_size
        this.rollouts = rollouts.rollouts.map(item => ({
          rollout_start_formatted: Object.hasOwn(item, 'rollout_start') ? moment(item.rollout_start).utc().format('D MMM YYYY HH:mm') : '',
          created_on_formatted: moment(item.created_on).utc().format('D MMM YYYY HH:mm'),
          withdrawn_on_formatted: Object.hasOwn(item, 'withdrawn_on') ? moment(item.withdrawn_on).utc().format('D MMM YYYY HH:mm') : '',
          ...item
        }))

        this.loading = false
      }
      catch {
        logger.info('error getting rollouts')
        this.loading = false
      }
    },

    launchRowAction(e) {
      let allOptions = document.querySelectorAll(".report-action-options");

      for (let option of allOptions) {
        option.classList.remove('active-options');
        if (option === e.target.nextSibling) {
          option.classList.toggle('active-options');
        } 
      }
    },

    toggleWithdrawPrompt(item) {
      this.withdrawState = false
      this.withdrawRes = null
      this.withdrawTarget = item
      this.withdrawRolloutConfirm = !this.withdrawRolloutConfirm
    },

    setPage(page) {
      if (!page) return
      this.selectedPage = page
    },

    async withdrawRolout() {
      try {
        this.withdrawState = true

        const withdraw = await updateFirmwareRollout(this.withdrawTarget.rollout_id)

        if(withdraw && Object.hasOwn(withdraw, 'result') && withdraw.result == 'Withdrawn') {
          this.rollouts = []
          this.getRollouts()
          this.withdrawState = false
          this.toggleWithdrawPrompt(null)
          this.refreshBuilds++
        }
      }

      catch {
        this.withdrawState = false
        throw new Error('A problem occurred.')
      }
    }
  }
}
</script>

<style lang="scss">
  .reports-action-launch {
    position: relative;
    cursor: pointer;
  }
  .report-action-options {
    display: none;
  }
  .active-options {
    display: block !important;  
  }
  .vert-center {
    align-items: center;
    display: flex;
    justify-content: flex-end;
  }

  .aux-pagination-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0;
    margin: 0;
  }

  #rollouts-table {
    .v-data-table__wrapper {
			overflow-x: visible !important;
			overflow-y: visible !important;
		}
  }
</style>